/**
 * AnnouncementsPopup - v2026.06.159
 * Carousel multi-slides des news popup non lues par l'user.
 *
 * Comportement :
 *  - Fetch GET /clients/announcements au mount
 *  - Si items.length > 0 -> affiche modal avec carrousel
 *  - Navigation prec/suivant
 *  - Bouton "Ne plus afficher" (si dismissable) -> POST dismiss + retire de la liste
 *  - Bouton "Fermer" -> ferme la modale sans dismiss (réapparaitra au prochain login)
 *  - Si la liste se vide (tous dismissed) -> modale se ferme automatiquement
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
  Button, Body1, Caption1, Title3, Badge, tokens, Spinner,
} from '@fluentui/react-components';
import {
  ArrowLeft20Regular, ArrowRight20Regular, Dismiss20Regular,
  CheckmarkCircle20Regular, Sparkle20Filled,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { announcementsApi, type ClientAnnouncement } from '../api/announcementsApi';
import { BrandLogo } from './BrandLogo';

export function AnnouncementsPopup() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [index, setIndex] = useState(0);
  const [hidden, setHidden] = useState(false);   // ferme local (session) sans dismiss serveur

  const { data, isLoading } = useQuery({
    queryKey: ['client-announcements'],
    queryFn:  () => announcementsApi.list(),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const items = useMemo<ClientAnnouncement[]>(() => data?.items ?? [], [data]);

  const dismissMutation = useMutation({
    mutationFn: (newsId: string) => announcementsApi.dismiss(newsId),
    onSuccess: () => {
      // Optimistic : on retire l'item du cache local et avance / ferme
      qc.setQueryData(['client-announcements'], (old: any) => {
        if (!old?.items) return old;
        const filtered = old.items.filter((_: any, i: number) => i !== index);
        return { ...old, items: filtered, total: filtered.length };
      });
      // Si on était sur le dernier, recule
      setIndex((prev) => {
        const newLen = (items.length - 1);
        if (prev >= newLen && newLen > 0) return newLen - 1;
        return prev;
      });
    },
  });

  // Reset hidden au changement d'user (les items reapparaitront au prochain login)
  useEffect(() => {
    if (items.length === 0) setHidden(false);
  }, [items.length]);

  if (isLoading || hidden) return null;
  if (items.length === 0) return null;

  const current = items[Math.min(index, items.length - 1)];
  if (!current) return null;

  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;

  return (
    <Dialog open={!hidden} onOpenChange={(_, d) => { if (!d.open) setHidden(true); }}>
      <DialogSurface style={{ maxWidth: 720 }}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<Dismiss20Regular />}
                onClick={() => setHidden(true)}
                aria-label={t('common.close', 'Fermer')}
              />
            }
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Sparkle20Filled primaryFill={tokens.colorBrandForeground1} />
              <span>{current.title}</span>
              {items.length > 1 && (
                <Badge appearance="outline" style={{ marginLeft: 8 }}>
                  {index + 1} / {items.length}
                </Badge>
              )}
            </div>
          </DialogTitle>
          <DialogContent>
            {/* Bandeau de marque : logo centré + filet dégradé NIS365 */}
            <div style={{
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              padding: '2px 0 16px', marginBottom: 18,
              borderBottom: '3px solid transparent',
              borderImage: 'linear-gradient(90deg, #1F3D5C 0%, #2E8B96 100%) 1',
            }}>
              <BrandLogo height={46} />
            </div>

            {current.coverImageUrl && (
              <img
                src={current.coverImageUrl}
                alt=""
                style={{
                  width: '100%', maxHeight: 240, objectFit: 'cover',
                  borderRadius: 8, marginBottom: 16,
                }}
              />
            )}
            {/* Body HTML (saisi par l'admin, trusted). Mise en forme via CSS scopée
                pour éviter les grands espaces du pre-wrap sur du contenu HTML. */}
            <style>{`
              .nis-announcement-body { font-size: 15px; line-height: 1.6; color: ${tokens.colorNeutralForeground1}; }
              .nis-announcement-body p { margin: 0 0 12px; }
              .nis-announcement-body ol, .nis-announcement-body ul { padding-left: 22px; margin: 10px 0; }
              .nis-announcement-body li { margin: 8px 0; padding-left: 4px; }
              .nis-announcement-body strong { color: #1F3D5C; }
              .nis-announcement-body a { color: #2E8B96; font-weight: 600; text-decoration: none; }
              .nis-announcement-body a:hover { text-decoration: underline; }
            `}</style>
            <div
              className="nis-announcement-body"
              dangerouslySetInnerHTML={{ __html: current.body }}
            />
            {current.publishedAt && (
              <Caption1 style={{ display: 'block', marginTop: 16, color: tokens.colorNeutralForeground3 }}>
                {t('announcements.publishedAt', 'Publié le')} {new Date(current.publishedAt).toLocaleDateString()}
              </Caption1>
            )}
          </DialogContent>
          <DialogActions>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', gap: 6 }}>
                {hasPrev && (
                  <Button appearance="subtle" icon={<ArrowLeft20Regular />}
                          onClick={() => setIndex(index - 1)}>
                    {t('common.prev', 'Précédent')}
                  </Button>
                )}
                {hasNext && (
                  <Button appearance="subtle" iconPosition="after" icon={<ArrowRight20Regular />}
                          onClick={() => setIndex(index + 1)}>
                    {t('common.next', 'Suivant')}
                  </Button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {current.dismissable && (
                  <Button
                    appearance="secondary"
                    icon={<CheckmarkCircle20Regular />}
                    disabled={dismissMutation.isPending}
                    onClick={() => dismissMutation.mutate(current.id)}
                  >
                    {dismissMutation.isPending
                      ? <Spinner size="tiny" />
                      : t('announcements.doNotShowAgain', 'Ne plus afficher')}
                  </Button>
                )}
                <Button appearance="primary" onClick={() => setHidden(true)}>
                  {t('common.close', 'Fermer')}
                </Button>
              </div>
            </div>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
